/* Lumetier animated logo — pure CSS + inline SVG, no external deps.
   Drop the `<LumetierLogo />` component anywhere. The aperture opens with a staggered
   blade-by-blade reveal, then the wordmark types in letter by letter, finally the
   champagne-gold rule draws and the tagline fades up. After the intro the aperture
   does a slow, almost-imperceptible breathe so the brand feels alive without being
   distracting. Lives at the route /lumetier-preview for review. */

const BLADES = 6;            // classic aperture / iris
const BLADE_DELAY = 0.08;    // stagger per blade
const INTRO = 1.8;           // intro duration baseline

function Blade({ idx }) {
  const angle = (360 / BLADES) * idx;
  return (
    <g
      style={{
        transformOrigin: "50% 50%",
        transform: `rotate(${angle}deg)`,
      }}
    >
      {/* Each blade is a single triangular wedge that rotates out from center.
          The intro animation expands + rotates simultaneously; after intro it
          breathes on a 6s loop to feel handcrafted. */}
      <polygon
        points="50,50 50,12 64,28"
        fill="currentColor"
        style={{
          transformOrigin: "50% 50%",
          animation: `lumetier-blade-in 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${idx * BLADE_DELAY}s both, lumetier-breathe 6s ease-in-out ${INTRO}s infinite`,
        }}
      />
    </g>
  );
}

export default function LumetierLogo({ size = 240, accent = "#C9A961", inverted = false }) {
  const word = "LUMETIER".split("");
  const fg = inverted ? "#fafaf9" : "#1c1917";
  const sub = inverted ? "rgba(250,250,249,0.55)" : "rgba(28,25,23,0.55)";

  return (
    /* Do NOT lock the outer div to `width: size` — that pinches the wordmark
       (which is wider than the aperture SVG because of its 0.42em letter
       spacing) and forces it to wrap to two lines like "LUMETI / ER". Keep the
       container content-sized; each child (SVG, wordmark, rule, tagline)
       self-centers via the `.lumetier-logo` text-align rule + SVG margin. */
    <div className="lumetier-logo" style={{ color: fg }}>
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        aria-hidden
        style={{ display: "block", margin: "0 auto" }}
      >
        {/* Aperture iris */}
        <g style={{ color: fg }}>
          {Array.from({ length: BLADES }).map((_, i) => (
            <Blade key={i} idx={i} />
          ))}
        </g>
        {/* Center lens dot */}
        <circle
          cx="50"
          cy="50"
          r="3.4"
          fill={accent}
          style={{ animation: `lumetier-dot 1.1s cubic-bezier(0.16, 1, 0.3, 1) ${INTRO - 0.5}s both` }}
        />
      </svg>

      <div
        className="lumetier-wordmark"
        style={{ marginTop: 28, color: fg }}
        aria-label="Lumetier"
      >
        {word.map((ch, i) => (
          <span
            key={i}
            style={{
              animation: `lumetier-letter-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${0.8 + i * 0.07}s both`,
            }}
          >
            {ch}
          </span>
        ))}
      </div>

      <div
        className="lumetier-rule"
        style={{
          background: accent,
          animation: `lumetier-rule-in 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${INTRO - 0.2}s both`,
        }}
      />

      <div
        className="lumetier-tagline"
        style={{
          color: sub,
          animation: `lumetier-fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${INTRO + 0.1}s both`,
        }}
      >
        everything behind your best work.
      </div>

      {/* Keyframes scoped via a single <style>. Keeping them collocated with the
          component means dropping this file into any project is one paste. */}
      <style>{`
        @keyframes lumetier-blade-in {
          0%   { transform: scale(0.15); opacity: 0; }
          60%  { opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes lumetier-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.03); }
        }
        @keyframes lumetier-dot {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.4); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
          /* Note: transform-origin set via class below since SVG attrs can't carry it. */
        }
        circle[cx="50"][cy="50"] { transform-origin: 50% 50%; }
        @keyframes lumetier-letter-in {
          0%   { transform: translateY(8px); opacity: 0; }
          100% { transform: translateY(0);   opacity: 1; }
        }
        @keyframes lumetier-rule-in {
          0%   { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes lumetier-fade-up {
          0%   { transform: translateY(6px); opacity: 0; }
          100% { transform: translateY(0);   opacity: 1; }
        }

        .lumetier-logo {
          font-family: 'Cormorant Garamond', 'EB Garamond', 'Playfair Display', Georgia, serif;
          text-align: center;
        }
        .lumetier-wordmark {
          font-size: 30px;
          letter-spacing: 0.42em;
          font-weight: 300;
          padding-left: 0.42em; /* compensate for trailing letter-spacing */
        }
        .lumetier-wordmark span { display: inline-block; }
        .lumetier-rule {
          width: 56px;
          height: 1px;
          margin: 16px auto;
          transform-origin: 50% 50%;
        }
        .lumetier-tagline {
          font-size: 12px;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
        }

        @media (prefers-reduced-motion: reduce) {
          .lumetier-logo * {
            animation: none !important;
            transform: none !important;
            opacity: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}
